import { AlertTriangle, GripVertical, Info, KeyRound, Loader2, X } from 'lucide-react';
import type { HTMLAttributes, ReactNode } from 'react';
import { Link } from 'react-router';
import type { DashboardWidget } from '@/api/types';
import { Button } from '@/components/ui';
import { DemoBadge } from '@/features/integrations/status';
import { cx } from '@/lib/cx';
import { relativeTime } from '@/lib/format';
import { displayValue, widgetTrend } from '@/lib/kpi';
import { ChangePill } from './ChangePill';
import { Sparkline } from './Sparkline';

export interface KpiCardProps {
  widget: DashboardWidget;
  comparedTo: string;
  customising?: boolean;
  /** dnd-kit listeners/attributes for the drag handle. */
  handleProps?: HTMLAttributes<HTMLButtonElement>;
  onRemove?: () => void;
  onReconnect?: () => void;
  reconnecting?: boolean;
  dragging?: boolean;
}

function StatusLine({ w, onReconnect, reconnecting }: { w: DashboardWidget; onReconnect?: () => void; reconnecting?: boolean }) {
  switch (w.status) {
    case 'syncing':
      return (
        <p className="flex items-center gap-1.5 text-xs text-muted-ink">
          <Loader2 className="size-3.5 animate-spin" aria-hidden /> Syncing…
        </p>
      );
    case 'stale':
      return (
        <p className="flex items-center gap-1.5 text-xs text-muted-ink">
          <Info className="size-3.5" aria-hidden /> Updated {relativeTime(w.updated_at)}. Showing the last numbers we have.
        </p>
      );
    case 'error':
      return (
        <p className="relative z-10 flex items-center gap-1.5 text-xs text-down">
          <AlertTriangle className="size-3.5" aria-hidden /> Couldn’t update.{' '}
          <Link to="/connections" className="underline underline-offset-2">
            See why
          </Link>
        </p>
      );
    case 'reauth_required':
      return (
        <div className="relative z-10 flex flex-wrap items-center justify-between gap-2">
          <p className="flex items-center gap-1.5 text-xs text-down">
            <KeyRound className="size-3.5" aria-hidden /> {w.integration_name} needs you to sign in again.
          </p>
          {onReconnect && (
            <Button size="sm" variant="primary" onClick={onReconnect} loading={reconnecting}>
              Reconnect
            </Button>
          )}
        </div>
      );
    case 'no_data':
      return <p className="text-xs text-muted-ink">No data yet for this period.</p>;
    default:
      return null;
  }
}

export function KpiCard({ widget: w, comparedTo, customising, handleProps, onRemove, onReconnect, reconnecting, dragging }: KpiCardProps) {
  const { trend, sentiment } = widgetTrend(w);
  const isText = w.unit === 'text';
  const value = displayValue(w);
  const titleId = `kpi-${w.id}`;

  const head: ReactNode = (
    <div className="flex items-start gap-2">
      <div className="min-w-0 flex-1">
        <p className="truncate text-xs text-muted-ink">
          {w.integration_name}
          <span aria-hidden> · </span>
          <span className="sr-only">, </span>
          {w.connection_name}
        </p>
        <h3 id={titleId} className="mt-0.5 truncate text-sm font-medium text-ink">
          {customising ? (
            w.label
          ) : (
            <Link to={`/kpi/${w.id}`} className="after:absolute after:inset-0 after:rounded-card">
              {w.label}
            </Link>
          )}
        </h3>
      </div>
      <div className="relative z-10 flex shrink-0 items-center gap-1">
        {w.is_demo && <DemoBadge />}
        {customising && (
          <>
            <button
              type="button"
              className="inline-flex size-8 cursor-grab touch-none items-center justify-center rounded-control text-muted-ink hover:bg-muted hover:text-ink active:cursor-grabbing"
              aria-label={`Move ${w.integration_name} ${w.label}`}
              {...handleProps}
            >
              <GripVertical className="size-4" aria-hidden />
            </button>
            <button
              type="button"
              onClick={onRemove}
              className="inline-flex size-8 items-center justify-center rounded-control text-muted-ink hover:bg-down/10 hover:text-down"
              aria-label={`Remove ${w.integration_name} ${w.label} from the dashboard`}
            >
              <X className="size-4" aria-hidden />
            </button>
          </>
        )}
      </div>
    </div>
  );

  return (
    <article
      aria-labelledby={titleId}
      data-testid="kpi-card"
      className={cx(
        'group relative flex h-full flex-col gap-3 rounded-card border border-border bg-card p-4 shadow-card transition-[border-color,box-shadow] sm:p-5',
        !customising && 'hover:border-ink/25',
        customising && 'ring-1 ring-transparent',
        dragging && 'z-20 shadow-xl ring-yellow',
        (w.status === 'error' || w.status === 'reauth_required') && 'border-down/40',
      )}
    >
      {head}
      <div className="flex flex-wrap items-end gap-x-3 gap-y-1">
        <p className={cx('kpi-number min-w-0 text-ink', isText ? 'truncate text-2xl' : 'text-[32px] leading-none')} title={isText ? value : undefined}>
          {value}
        </p>
        {!isText && (
          <span className="flex items-center gap-1.5 pb-0.5">
            <ChangePill pct={w.change_pct} trend={trend} sentiment={sentiment} comparedTo={comparedTo} />
          </span>
        )}
      </div>
      {isText ? (
        <p className="-mt-1 text-xs text-muted-ink">{w.text_detail ?? ' '}</p>
      ) : (
        <p className="-mt-1 text-xs text-muted-ink">vs {comparedTo}</p>
      )}
      <div className="mt-auto">
        <Sparkline points={w.sparkline} />
      </div>
      {w.note && (
        <p className="flex items-start gap-1.5 text-xs text-muted-ink">
          <Info className="mt-px size-3.5 shrink-0" aria-hidden />
          {w.note}
        </p>
      )}
      <StatusLine w={w} onReconnect={onReconnect} reconnecting={reconnecting} />
      {w.status === 'stale' && <span className="sr-only">This number may be out of date.</span>}
    </article>
  );
}

export function KpiCardSkeleton() {
  return (
    <div aria-hidden className="flex h-[212px] flex-col gap-3 rounded-card border border-border bg-card p-5">
      <div className="skeleton h-3 w-1/2" />
      <div className="skeleton h-4 w-1/3" />
      <div className="skeleton mt-1 h-8 w-2/3" />
      <div className="skeleton mt-auto h-10 w-full" />
    </div>
  );
}
